import React from 'react';
import styles from './Travelers.module.css';
import { CheckoutSection } from '../CheckoutSection';
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider';
import { useIntl } from '@/intl/useIntl';
import { ListItem } from '@sunwebgroup/uds-ui/list-item';
import { AgeCategoryValues, CheckoutParticipant, OverviewService, ParticipantTypeValues } from '../../domain/types';
import { Typography } from '@sunwebgroup/uds-ui/typography';
import { Tag } from '@sunwebgroup/uds-ui/tag';
import {
  formatDate,
  formatParticipantFullName,
  getParticipantAge,
} from './travelerHelper';
import { getParticipantTitleTextKey } from '../../domain/participantTitleText';
import { parseISO } from 'date-fns';
import { Button } from '@sunwebgroup/uds-ui/button';
import { TravelerDetailsSheet } from './TravelerDetailsSheet';
import { useBookingDraftQuery } from '../../domain/hooks';


function Travelers() {
  const { data } = useBookingDraftQuery()
  const intl = useIntl();
  const participantsList = Object.values(data?.participants ?? {});
  const [isParticipantSheetOpen, setIsParticipantSheetOpen] = React.useState(false);
  const [selectedParticipant, setSelectedParticipant] = React.useState<CheckoutParticipant | null>(null);
  const openerRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    if (!isParticipantSheetOpen) {
      openerRef.current?.focus();
    }
  }, [isParticipantSheetOpen]);

  if (data?.confirmation === undefined || data?.participants === undefined) {
    return null;
  }

  return (
    <>
      <CheckoutSection title={intl.formatMessage({ id: 'checkout_steps_travelers_title' })} glyph="Family">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--primitives-space-8)' }}>
          {participantsList.map((participant) => (
            <Traveler
              key={participant.id}
              participant={participant}
              departureDate={data?.confirmation?.departureDate ?? ''}
              selectParticipant={(opener) => {
                openerRef.current = opener;
                setSelectedParticipant(participant);
                setIsParticipantSheetOpen(true);
              }}
            />
          ))}
        </div>
      </CheckoutSection>
      <TravelerDetailsSheet
        isOpen={isParticipantSheetOpen}
        participant={selectedParticipant}
        setIsOpen={setIsParticipantSheetOpen}
        services={data?.confirmation?.services ?? []}
      />
    </>
  );
}

function Traveler({
  participant,
  departureDate,
  selectParticipant,
}: {
  participant: CheckoutParticipant;
  departureDate: string | Date;
  selectParticipant: (opener: HTMLButtonElement) => void;
}) {
  const intl = useIntl();
  const locale = useConfiguration('locale');
  const formattedBirthDate = formatDate(participant.birthdate, locale, 'short');
  const age = getParticipantAge(
    participant.birthdate,
    typeof departureDate === 'string' ? parseISO(departureDate) : departureDate
  );
  const ageLabel =
    age === null
      ? ''
      : age.unit === 'months'
      ? intl.formatMessage({ id: 'checkout_traveler_age_months' }, { count: age.count })
      : intl.formatMessage({ id: 'checkout_traveler_age_years' }, { count: age.count });
  const isMainBookerParticipantType = participant.participantType === ParticipantTypeValues.MAIN_BOOKER;
  const typeLabel =
    participant.ageCategory === AgeCategoryValues.BABY || participant.ageCategory === AgeCategoryValues.CHILD
      ? intl.formatMessage({
          id: participant.ageCategory === AgeCategoryValues.BABY ? 'age_category_baby' : 'age_category_child',
        })
      : null;
  const typeLabelCapitalized = typeLabel ? typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1) : null;
  const titleKey = getParticipantTitleTextKey(participant.title);

  return (
    <ListItem key={participant.id} variant="outlined" className={styles.travelerItem}>
      <div className={styles.travelersContent}>
        <div className={styles.travelerInfo}>
          {isMainBookerParticipantType && (
            <Tag variant="informative-tinted">{intl.formatMessage({ id: 'checkout_traveler_main_booker' })}</Tag>
          )}
          <div>
            <Typography as="p" variant="body-m-bold-tight">
              {formatParticipantFullName(
                titleKey ? intl.formatMessage({ id: titleKey }) : null,
                participant.name,
                participant.surname
              )}
            </Typography>
            {formattedBirthDate && (
              <Typography className={styles.travelerDetails} as="p" variant="body-s-regular-tight">
                {formattedBirthDate}
                {ageLabel ? ` (${ageLabel})` : ''}
                {typeLabelCapitalized ? ` - ${typeLabelCapitalized}` : ''}
              </Typography>
            )}
          </div>
        </div>
        <Button onClick={(event: React.MouseEvent<HTMLButtonElement>) => selectParticipant(event.currentTarget)} className={styles.travelerButton} size="S" variant="secondary">
          {intl.formatMessage({ id: 'checkout_traveler_view_button' })}
        </Button>
      </div>
    </ListItem>
  );
}

export default Travelers;
